import { useCallback, useEffect, useState } from "react";
import { InventoryOutlookReportView } from "./InventoryOutlookReportView";
import { InventoryOutlookPurchase } from "./InventoryOutlookPurchase";
import { InventoryOutlookCadence } from "./InventoryOutlookCadence";
import { InventoryOutlookIndicatorMap } from "./InventoryOutlookIndicatorMap";
import { InventoryOutlookOrderMapping } from "./InventoryOutlookOrderMapping";
import { ManufacturingTabbedSection, type TabbedSectionTab } from "./ManufacturingTabbedSection";
import {
  getInventoryOutlook,
  getInventoryOutlookSettings,
  saveInventoryOutlookSettings,
  setOutlookCadenceRecipient,
  type InventoryOutlookReport,
  type InventoryOutlookSettings,
  type OutlookCadenceRecipient,
} from "@/lib/manufacturing/inventoryMatchClient";

type OutlookSlide = "outlook" | "mapping" | "indicators" | "emails";

const SLIDES: ReadonlyArray<TabbedSectionTab<OutlookSlide>> = [
  { id: "outlook", label: "Outlook" },
  { id: "mapping", label: "Order mapping" },
  { id: "indicators", label: "Map order indicators" },
  { id: "emails", label: "Outlook emails" },
];

export function ManufacturingInventoryMatchPanel({
  enabled,
  onDraftCreated,
}: {
  enabled: boolean;
  onDraftCreated?: (poId: string) => void;
}) {
  const [slide, setSlide] = useState<OutlookSlide>("outlook");
  const [report, setReport] = useState<InventoryOutlookReport | null>(null);
  const [settings, setSettings] = useState<InventoryOutlookSettings | null>(null);
  const [recipients, setRecipients] = useState<OutlookCadenceRecipient[]>([]);
  const [canManageRecipients, setCanManageRecipients] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reloadSettings = useCallback(async () => {
    const settingsRes = await getInventoryOutlookSettings();
    if (!settingsRes.ok) return setError(settingsRes.error);
    setSettings(settingsRes.data.settings);
    setRecipients(settingsRes.data.recipients);
    setCanManageRecipients(settingsRes.data.can_manage_recipients);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void reloadSettings();
  }, [enabled, reloadSettings]);

  function applySettings(data: {
    settings: InventoryOutlookSettings;
    recipients: OutlookCadenceRecipient[];
    can_manage_recipients: boolean;
  }) {
    setSettings(data.settings);
    setRecipients(data.recipients);
    setCanManageRecipients(data.can_manage_recipients);
  }

  async function generate() {
    setBusy(true);
    setError(null);
    const out = await getInventoryOutlook();
    setBusy(false);
    if (!out.ok) return setError(out.error);
    setReport(out.data.report);
  }

  async function saveSettings(patch: Partial<InventoryOutlookSettings>) {
    setError(null);
    const out = await saveInventoryOutlookSettings(patch);
    if (!out.ok) return setError(out.error);
    applySettings(out.data);
  }

  async function setRecipient(userId: string, optedIn: boolean) {
    setError(null);
    const out = await setOutlookCadenceRecipient(userId, optedIn);
    if (!out.ok) return setError(out.error);
    applySettings(out.data);
  }

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {error}
        </div>
      ) : null}
      <ManufacturingTabbedSection<OutlookSlide> bare tabs={SLIDES} activeTab={slide} onTabChange={setSlide}>
        {slide === "outlook" ? (
          <div className="space-y-4">
            <button
              type="button"
              disabled={busy}
              className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
              onClick={() => void generate()}
            >
              Generate 7 / 14 / 30 day report
            </button>
            {report ? <InventoryOutlookReportView report={report} /> : null}
            {report ? <InventoryOutlookPurchase report={report} onDraftCreated={(poId) => onDraftCreated?.(poId)} /> : null}
          </div>
        ) : null}
        {slide === "mapping" ? <InventoryOutlookOrderMapping report={report} /> : null}
        {slide === "indicators" ? <InventoryOutlookIndicatorMap enabled={enabled} /> : null}
        {slide === "emails" ? (
          <details className="rounded-xl border border-gray-200 dark:border-zinc-700">
            <summary className="cursor-pointer px-3 py-2 text-sm font-semibold">Scheduled outlook email</summary>
            <div className="border-t border-gray-200 px-3 py-3 dark:border-zinc-700">
              {settings ? (
                <InventoryOutlookCadence
                  settings={settings}
                  recipients={recipients}
                  canManage={canManageRecipients}
                  onSave={(patch) => void saveSettings(patch)}
                  onSetRecipient={(userId, optedIn) => void setRecipient(userId, optedIn)}
                />
              ) : null}
            </div>
          </details>
        ) : null}
      </ManufacturingTabbedSection>
    </div>
  );
}
